import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { QueryKeys } from '../../../../constants/queryKeys.constants'
import { createBeachPlan, deleteBeachPlan, listBeachPlans, reorderBeachPlans, saveBeachPlan } from '../../../../services/beachPlans.service'
import { BeachPlanDocument, BeachPlanFirestoreData, MAX_BEACH_PLANS } from '../../../../types/beachPlan.types'

export const useBeachPlans = (enabled = true) => {
  const queryClient = useQueryClient()

  const { data: beachPlans = [], isLoading } = useQuery<BeachPlanFirestoreData[]>({
    queryKey: [QueryKeys.BEACH_PLANS],
    queryFn: listBeachPlans,
    enabled,
  })

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKeys.BEACH_PLANS] })
  }, [queryClient])

  const createPlan = useCallback(
    async (data: Omit<BeachPlanDocument, 'updatedAt'>) => {
      const id = await createBeachPlan(data)
      invalidate()
      return id
    },
    [invalidate]
  )

  const updatePlan = useCallback(
    async (beachPlanId: string, data: Partial<BeachPlanDocument>) => {
      await saveBeachPlan(beachPlanId, data)
      invalidate()
    },
    [invalidate]
  )

  const removePlan = useCallback(
    async (beachPlanId: string) => {
      await deleteBeachPlan(beachPlanId)
      invalidate()
    },
    [invalidate]
  )

  const reorderPlans = useCallback(
    async (orderedIds: string[]) => {
      await reorderBeachPlans(orderedIds)
      invalidate()
    },
    [invalidate]
  )

  return {
    beachPlans,
    isLoading,
    canAddPlan: beachPlans.length < MAX_BEACH_PLANS,
    createPlan,
    updatePlan,
    removePlan,
    reorderPlans,
    invalidate,
  }
}

export const useEnabledBeachPlans = (enabled = true) => {
  const { beachPlans, isLoading, ...rest } = useBeachPlans(enabled)
  const enabledPlans = beachPlans.filter((plan) => plan.isEnabled)

  return {
    beachPlans: enabledPlans,
    isLoading,
    ...rest,
  }
}
